import { Button } from "@/components";
import NumberField from "@/components/form/components/NumberField";
import InputField from "@/components/InputField";
import { Form } from "@/components/ui/form";
import {
  confirmFirebaseSmsOtp,
  RECAPTCHA_CONTAINER_ID,
  resetRecaptcha,
  sendFirebaseSmsOtp,
} from "@/lib/firebase";
import { getGoogleAccessToken } from "@/lib/google";
import { notifyError, notifySuccess } from "@/lib/notification";
import { cn } from "@/lib/utils";
import { authService } from "@/services/auth.service";
import { useAuthStore } from "@/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import type { ConfirmationResult } from "firebase/auth";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const loginSchema = z.object({
  identifier: z
    .string()
    .min(1, "Please enter an email or phone number.")
    .refine((val) => {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
      const isPhone = /^\d{10}$/.test(val);
      return isEmail || isPhone;
    }, "Please enter a valid email address or 10-digit phone number."),
});

const verifySchema = z.object({
  otp: z.string().min(4, "OTP must be at least 4 digits."),
});

type LoginFormType = z.infer<typeof loginSchema>;
type VerifyFormType = z.infer<typeof verifySchema>;

const Login = () => {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  const [step, setStep] = useState<"SEND_OTP" | "VERIFY_OTP">("SEND_OTP");
  const [identifier, setIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState<"email" | "phone">(
    "email"
  );
  // Holds the Firebase ConfirmationResult between "send SMS" and "confirm code"
  // for the phone flow.
  const confirmationRef = useRef<ConfirmationResult | null>(null);

  const form = useForm<LoginFormType>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      identifier: "",
    },
  });

  const verifyForm = useForm<VerifyFormType>({
    resolver: zodResolver(verifySchema),
    defaultValues: {
      otp: "",
    },
  });

  const otpMutation = useMutation({
    mutationFn: async ({
      type,
      identifier,
    }: {
      type: "email" | "phone";
      identifier: string;
    }) => {
      // Step 1: enrollment check. For phone this does NOT send an SMS — it only
      // validates that the number is enrolled (404 otherwise). For email the
      // backend sends the email OTP here.
      await authService.sendOtp(type, identifier);

      // Step 2 (phone only): Firebase sends the SMS.
      if (type === "phone") {
        confirmationRef.current = await sendFirebaseSmsOtp(identifier);
      }
    },
    onSuccess: (_, variables) => {
      setIdentifier(variables.identifier);
      setIdentifierType(variables.type);
      setStep("VERIFY_OTP");
      notifySuccess(
        variables.type === "phone"
          ? "OTP sent to your phone!"
          : "OTP sent to your email!"
      );
    },
    onError: (error: any) => {
      notifyError(
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error?.message ||
        "Failed to send OTP. Please try again."
      );
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async ({ otp }: { otp: string }) => {
      // For phone: `otp` is the 6-digit SMS code. Confirm it with Firebase and
      // send the resulting Firebase ID token to the backend (NOT the 6 digits).
      // For email: the typed code is sent as-is.
      let otpToSend = otp;
      if (identifierType === "phone") {
        if (!confirmationRef.current) {
          throw new Error("Session expired. Please request the OTP again.");
        }
        otpToSend = await confirmFirebaseSmsOtp(confirmationRef.current, otp);
      }
      return await authService.verifyOtp(identifierType, identifier, otpToSend);
    },
    onSuccess: (responseData) => {
      // API might return standard structure or wrap it inside `data.data` based on the console log
      // Using 'any' here to prevent TypeScript errors since the exact payload shape misaligns with AuthResponse
      const data: any = responseData;
      const payload = data.data && data.data.access_token ? data.data : data;
      
      const token = payload.access_token || payload.token;
      const user = payload.user || payload.data || { id: "student-id" }; 
      
      if (token) {
        login(user, token, payload.refresh_token);
        notifySuccess("Login successful!");
        navigate("/dashboard");
      } else {
        notifyError("Token missing from response! Check console for data.");
        console.error("Missing token in OTP verify response:", data);
      }
    },
    onError: (error: any) => {
      notifyError(
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error?.message ||
        "Invalid OTP. Please try again."
      );
    },
  });

  // TanStack Query mutation for Google
  const mutation = useMutation({
    mutationFn: async (googleToken: string) => {
      return await authService.loginWithGoogle(googleToken);
    },
    onSuccess: (responseData) => {
      // API might return standard structure or wrap it inside `data.data` based on the console log
      // Using 'any' here to prevent TypeScript errors since the exact payload shape misaligns with AuthResponse
      const data: any = responseData;
      const payload = data.data && data.data.access_token ? data.data : data;
      
      const token = payload.access_token || payload.token;
      const user = payload.user || payload.data || { id: "student-id" }; 
      
      if (token) {
        login(user, token, payload.refresh_token);
        notifySuccess("Login successful!");
        navigate("/dashboard");
      } else {
        notifyError("Token missing from response! Check console for data.");
        console.error("Missing token in Google verify response:", data);
      }
    },
    onError: (error: any) => {
      notifyError(
        error.response?.data?.error?.message ||
        error?.message ||
        "Login failed. Please try again."
      );
    },
  });

  const onSubmit = (data: LoginFormType) => {
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.identifier);
    otpMutation.mutate({
      type: isEmail ? "email" : "phone",
      identifier: data.identifier,
    });
  };

  const onVerifySubmit = (data: VerifyFormType) => {
    verifyOtpMutation.mutate({ otp: data.otp });
  };

  const handleLogin = async () => {
    try {
      const googleToken = await getGoogleAccessToken();
      mutation.mutate(googleToken);
    } catch (err: any) {
      notifyError(err?.message || "Google login failed.");
    }
  };

  if (import.meta.env.VITE_MODE === "development") {
    return (
      <div className="flex h-screen items-center justify-center gap-4">
        🔐 Login Page (Public)
        <Button onClick={() => navigate("/dev-login")}>Dev Login</Button>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen w-screen items-center justify-center overflow-hidden bg-slate-50">
      {/* Invisible reCAPTCHA mount point required by Firebase Phone Auth */}
      <div id={RECAPTCHA_CONTAINER_ID} />

      {/* Animated Background Elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-indigo-500/20 blur-[120px] rounded-full animate-pulse" />
      <div className="absolute bottom-[-10%] right-[-5%] w-[40%] h-[60%] bg-purple-500/20 blur-[120px] rounded-full animate-pulse" style={{ animationDelay: "2s" }} />
      <div className="absolute top-[20%] right-[20%] w-[30%] h-[40%] bg-cyan-400/20 blur-[100px] rounded-full animate-pulse" style={{ animationDelay: "4s" }} />

      <div className="relative z-10 w-full max-w-md p-6">
        <div className="flex flex-col rounded-[2.5rem] border border-white/80 bg-white/70 backdrop-blur-3xl p-10 shadow-[0_8px_40px_rgb(0,0,0,0.06)] transition-all duration-500">
          
          <div className="mb-8 text-center flex flex-col items-center">
            {/* Logo placeholder icon */}
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-xl shadow-indigo-600/30 transform transition-transform hover:scale-105 duration-300">
               <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 14l9-5-9-5-9 5 9 5z" />
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
               </svg>
            </div>
            
            <h1 className="text-3xl font-black text-slate-800 tracking-tight">Welcome Student</h1>
            <p className="mt-3 text-sm font-medium text-slate-500 max-w-[250px] leading-relaxed mx-auto">
              {step === "SEND_OTP"
                ? "Sign in to access your exclusive internship portal"
                : `We've sent a verification code to ${
                    identifierType === "phone" ? "+91 " : ""
                  }${identifier}`}
            </p>
          </div>

          {step === "SEND_OTP" && (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <div className="space-y-1">
                   <p className="text-xs font-bold text-slate-500 uppercase tracking-widest pl-1 mb-2">Login Details</p>
                   <InputField
                     autoFocus
                     type="text"
                     name="identifier"
                     placeholder="Email or Phone Number"
                     disabled={otpMutation.isPending || mutation.isPending}
                     onChange={(e) => {
                       const val = e.target.value;
                       // All-digit input is treated as a phone number and
                       // capped at 10 digits; anything with non-digits (email)
                       // is left untouched.
                       if (/^\d*$/.test(val)) {
                         form.setValue("identifier", val.slice(0, 10), {
                           shouldValidate: true,
                         });
                       }
                     }}
                   />
                </div>

                <Button
                  type="submit"
                  className={cn(
                    "w-full h-12 rounded-xl text-sm font-bold shadow-md transition-all hover:shadow-lg",
                    otpMutation.isPending || mutation.isPending
                      ? "opacity-50 hover:cursor-not-allowed"
                      : "hover:cursor-pointer"
                  )}
                  disabled={otpMutation.isPending || mutation.isPending}
                >
                  {otpMutation.isPending ? "Sending Code..." : "Continue with OTP"}
                </Button>
              </form>
            </Form>
          )}

          {step === "VERIFY_OTP" && (
            <Form {...verifyForm}>
              <form
                onSubmit={verifyForm.handleSubmit(onVerifySubmit)}
                className="space-y-5"
              >
                <div className="space-y-1">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest pl-1 mb-2">Verification Code</p>
                  <NumberField
                    autoFocus
                    name="otp"
                    placeholder="Enter 6-digit code"
                    disabled={verifyOtpMutation.isPending}
                  />
                </div>

                <Button
                  type="submit"
                  className={cn(
                    "w-full h-12 rounded-xl text-sm font-bold shadow-md transition-all hover:shadow-lg",
                    verifyOtpMutation.isPending
                      ? "opacity-50 hover:cursor-not-allowed"
                      : "hover:cursor-pointer"
                  )}
                  disabled={verifyOtpMutation.isPending}
                >
                  {verifyOtpMutation.isPending ? "Verifying..." : "Verify OTP"}
                </Button>
                
                <div className="mt-2 text-center pt-2">
                  <Button
                    type="button"
                    className={cn(
                      "w-full h-12 rounded-xl border border-slate-200 text-slate-600 font-bold bg-white/50 transition-colors hover:bg-white",
                      verifyOtpMutation.isPending
                        ? "opacity-50 hover:cursor-not-allowed"
                        : "hover:cursor-pointer"
                    )}
                    onClick={() => {
                      confirmationRef.current = null;
                      resetRecaptcha();
                      setStep("SEND_OTP");
                    }}
                    disabled={verifyOtpMutation.isPending}
                    variant={"outline"}
                  >
                    Back to login
                  </Button>
                </div>
              </form>
            </Form>
          )}

          {step === "SEND_OTP" && (
            <>
              <div className="relative my-7">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200/60"></div>
                </div>
                <div className="relative flex justify-center text-xs font-black uppercase tracking-widest">
                  <span className="bg-transparent px-3 text-slate-400">OR</span>
                </div>
              </div>

              <Button
                className={cn(
                  "flex w-full h-12 items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white text-slate-700 font-bold transition-all shadow-sm hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5",
                  mutation.isPending
                    ? "hover:cursor-not-allowed opacity-70"
                    : "hover:cursor-pointer"
                )}
                onClick={handleLogin}
                disabled={otpMutation.isPending || mutation.isPending}
                variant="outline"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                {mutation.isPending ? "Logging in..." : "Continue with Google"}
              </Button>


            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
